import { useEffect, useRef, useState } from "react";
import { isValidGps, subscribeGps, type GpsMessage, type Subscribe } from "../api/ws";
import type { GpsPosition } from "../api/types";

export function useGpsFeed(sub: Subscribe | null, seed: GpsPosition | null = null) {
  const seedRef = useRef(seed);
  seedRef.current = seed;

  const [positions, setPositions] = useState<Map<number, GpsPosition>>(() => {
    const m = new Map<number, GpsPosition>();
    if (seed && isValidGps(seed)) m.set(seed.train_id, seed);
    return m;
  });
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    setPositions((prev) => {
      const next = new Map(prev);
      if (seed && isValidGps(seed)) next.set(seed.train_id, seed);
      return next;
    });
  }, [seed?.train_id, seed?.gps_updated_at, seed?.latitude, seed?.longitude]);

  useEffect(() => {
    if (!sub) return;

    const onMessage = (msg: GpsMessage) => {
      const currentSeed = seedRef.current;
      setPositions((prev) => {
        const next = new Map(prev);
        if (msg.type === "snapshot") {
          next.clear();
          if (currentSeed && isValidGps(currentSeed)) next.set(currentSeed.train_id, currentSeed);
          for (const p of msg.positions ?? []) {
            if (isValidGps(p)) next.set(p.train_id, p);
          }
        } else if (msg.type === "update" && isValidGps(msg.position)) {
          next.set(msg.position.train_id, msg.position);
        }
        return next;
      });
    };

    return subscribeGps(sub, onMessage, setConnected);
  }, [sub?.kind, sub && sub.kind === "train" ? sub.train_id : 0]);

  return { positions, connected };
}
